import Styles from './TemplateSelect.module.scss'
import { useRef } from 'react'
import { useVisualTemplates } from 'hooks/useVisualTemplates'
import { Carousel, DEFAULT_OPTIONS, InnerCarousel } from 'components/Carousel/Carousel'
import { collectResults } from 'utils/query'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'
import { Heading, HeadingLevel } from 'blaze-ui/Heading/Heading'
import { TemplatePreview } from './TemplatePreview'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import useEmblaCarousel from 'embla-carousel-react'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { PreviousButton } from 'screens/blaze/Home/components/Catalogue/components/ScrollButton/PreviousButton'
import { NextButton } from 'screens/blaze/Home/components/Catalogue/components/ScrollButton/NextButton'
import { ITEM_HEIGHT, ITEM_WIDTH, LARGE_ITEM_HEIGHT, LARGE_ITEM_WIDTH, WIDE_ASPECT_RATIO } from './TemplateSelect'
import { IFilters } from 'components/Wizards/DesignWizard/TemplateSelection/Filters'

interface Props {
  category: string
  filters: Omit<IFilters, 'categories'>
  onSelect: (templateId: number) => void
  selectedIds?: number[]
  width: number
  height: number
  largePreviews?: boolean
  headingLevel?: HeadingLevel
  carouselOffset?: number
}

export const CategoryCarousel = ({
  width,
  height,
  category,
  filters,
  onSelect,
  selectedIds,
  largePreviews,
  headingLevel = 3,
  carouselOffset,
}: Props) => {
  const isWide = width / height > WIDE_ASPECT_RATIO

  let itemWidth: number | undefined
  let itemHeight: number | undefined

  if (largePreviews) {
    itemWidth = isWide ? undefined : LARGE_ITEM_WIDTH
    itemHeight = isWide ? LARGE_ITEM_HEIGHT : undefined
  } else {
    itemWidth = isWide ? undefined : ITEM_WIDTH
    itemHeight = isWide ? ITEM_HEIGHT : undefined
  }
  const ITEMS_TO_LOAD = 10
  const elementRef = useRef<HTMLDivElement>(null)
  const intersection = useIntersectionObserver(elementRef, { threshold: [0, 0.00000001] })
  const isVisible = (intersection?.intersectionRatio ?? 0) >= 0.00000001
  const { types, styles, query } = filters

  const visualTemplatesQuery = useVisualTemplates(
    {
      height,
      width,
      element_type: 'Template',
      category: [category],
      items: ITEMS_TO_LOAD,
      type: types === 'all' ? undefined : ([...types] as string[]),
      style: styles === 'all' ? undefined : ([...styles] as string[]),
      query: query === '' ? undefined : query,
      order_by: 'random',
    },
    {
      enabled: isVisible,
      cacheTime: 2 * 1000 * 60,
      staleTime: Infinity,
    },
  )

  const templates = collectResults(visualTemplatesQuery, (data) => data.visual_elements)

  const embla = useEmblaCarousel({
    ...DEFAULT_OPTIONS,
    dragFree: true,
    align: 'end',
    slidesToScroll: templates.length > 1 ? 'auto' : 1,
  })
  const emblaApi = embla[1]
  const { isMobile } = useResponsiveDesign()

  const { fetchNextPage, hasNextPage } = visualTemplatesQuery

  let offset = carouselOffset ?? (isMobile ? 20 : 35)

  // Put carousel content and the loaders in vars so they can be re-used, we can't put it in a component
  // cause the carousel only accepts an array of elements, they can't be wrapped in anything
  const carouselContent = templates.map((template, i) => {
    const isSelected = selectedIds?.includes(template.id)
    return (
      <TemplatePreview
        key={`${template.id}-${i}`}
        template={template}
        onPress={() => {
          onSelect(template.id)
        }}
        isSelected={isSelected}
        aspectRatio={template.width / template.height}
        itemWidth={itemWidth}
        itemHeight={itemHeight}
        largePreview={largePreviews}
      />
    )
  })

  const infiniteScroll = hasNextPage && (
    <InfiniteScroll hasMore={hasNextPage} loadMore={fetchNextPage}>
      <ShimmeringColor aspectRatio={width / height} width={itemWidth} height={itemHeight} />
    </InfiniteScroll>
  )

  const loadMore =
    hasNextPage &&
    Array.from({ length: 2 }).map((_, i) => (
      <ShimmeringColor key={i} aspectRatio={width / height} width={itemWidth} height={itemHeight} />
    ))

  return (
    <div ref={elementRef} className={Styles.category} data-test-id={category}>
      <div className={Styles.title} style={{ paddingLeft: offset }}>
        <Heading level={headingLevel}>{category}</Heading>
        {emblaApi && templates.length > 1 && !isMobile && (
          <div className={Styles.controls}>
            <PreviousButton emblaApi={emblaApi} />
            <NextButton emblaApi={emblaApi} />
          </div>
        )}
      </div>

      <div className={Styles.templatesCarousel}>
        {visualTemplatesQuery.isLoading && (
          <Carousel withControls={false} offset={offset}>
            {Array.from({ length: 10 }).map((_, i) => (
              <ShimmeringColor aspectRatio={width / height} width={itemWidth} height={itemHeight} key={i} />
            ))}
          </Carousel>
        )}
        {!visualTemplatesQuery.isLoading && (
          <>
            {isMobile ? (
              <Carousel withControls={false} offset={offset}>
                {carouselContent}
                {infiniteScroll}
                {loadMore}
              </Carousel>
            ) : (
              <InnerCarousel embla={embla} withControls={false} offset={offset}>
                {carouselContent}
                {infiniteScroll}
                {loadMore}
              </InnerCarousel>
            )}
          </>
        )}
      </div>
    </div>
  )
}
